import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Folder, Task } from '../../types';
import { generateId } from '../../utils/Utils';
import { getLocalDateKey } from './dateUtils';
import { getFlexibleIntervalForTask } from './flexibleDateUtils';
import { getFlexiblePrecision, getTaskEnd, normalizeFlexibleDate } from './scheduleUtils';
import { getTimelineTaskStartDate } from './taskCreationDefaults';
import { PlannerTaskInfo } from './types';
import { PlannerData } from './usePlannerData';
import { PlannerRefs } from './usePlannerRefs';
import { PlannerState } from './usePlannerState';

type ScheduleValues = Pick<Task, 'dueDate' | 'schedule' | 'reminderTime' | 'recurrence'>;

export const useTaskModal = (state: PlannerState, refs: PlannerRefs, data: PlannerData, folders: Folder[]) => {
    const { t } = useTranslation();
    const {
        editingCollectionId, editingTask, existingTaskPickerDate, newTaskTitle,
        selectedDate, showSchedule, timelineScale, setActiveTimelineDateKey, setActiveTimelineLabel, setAddTaskDate, setAddTaskDuration, setAddTaskTime,
        setDraftSelection, setDragStartOffset, setEditingCollectionId, setEditingTask, setExistingTaskPickerDate,
        setFixedScheduleDate, setFlexibleEndDate, setFlexibleInterval, setFlexiblePrecision, setIsFlexibleTask,
        setNewTaskTitle, setSelectedDate, setShowAddTask, setTimelineScale, setTimelineWindow,
    } = state;
    const {
        isProgrammaticTimelineNavigationRef, pendingTimelineCurrentScrollRef, pendingTimelineTaskIdScrollRef,
        pendingTimelineTaskScrollAttemptsRef, pendingTimelineTaskScrollRef, suppressNextTimelineCurrentScrollRef,
    } = refs;
    const { updateCollection, updateTask } = data;
    const reset = useCallback(() => {
        setNewTaskTitle('');
        setAddTaskDate(null);
        setAddTaskTime(null);
        setAddTaskDuration(60);
        setIsFlexibleTask(false);
        setFlexiblePrecision('day');
        setFlexibleInterval(1);
        setFlexibleEndDate(null);
        setFixedScheduleDate(null);
        setDragStartOffset(0);
        setDraftSelection(null);
    }, [setAddTaskDate, setAddTaskDuration, setAddTaskTime, setDraftSelection, setDragStartOffset,
        setFixedScheduleDate, setFlexibleEndDate, setFlexibleInterval, setFlexiblePrecision,
        setIsFlexibleTask, setNewTaskTitle]);
    const openCreateTaskModal = useCallback((startDate?: Date, delay = 0) => {
        const target = new Date(startDate || selectedDate);
        if (target.getHours() === 0 && target.getMinutes() === 0) target.setHours(9, 0, 0, 0);
        setEditingTask(null);
        setEditingCollectionId(null);
        setNewTaskTitle('');
        setAddTaskDate(target);
        setAddTaskTime(`${target.getHours().toString().padStart(2, '0')}:${target.getMinutes().toString().padStart(2, '0')}`);
        setIsFlexibleTask(false);
        setFlexiblePrecision('day');
        setFlexibleInterval(1);
        setFlexibleEndDate(null);
        if (delay > 0) window.setTimeout(() => setShowAddTask(true), delay);
        else setShowAddTask(true);
    }, [selectedDate, setAddTaskDate, setAddTaskTime, setEditingCollectionId, setEditingTask,
        setFlexibleEndDate, setFlexibleInterval, setFlexiblePrecision, setIsFlexibleTask,
        setNewTaskTitle, setShowAddTask]);
    const handleAddTask = (scheduleValues: ScheduleValues) => {
        if (!newTaskTitle.trim()) return;

        const scheduled = scheduleValues.dueDate ? new Date(scheduleValues.dueDate) : undefined;
        const updates: Partial<Task> = {
            title: newTaskTitle.trim(),
            duration: undefined,
            ...scheduleValues,
        };
        if (editingTask && editingCollectionId) {
            updateTask(editingCollectionId, editingTask.id, updates);
            if (!showSchedule && timelineScale === 'day' && scheduled) {
                pendingTimelineTaskScrollRef.current = scheduled;
                pendingTimelineTaskIdScrollRef.current = editingTask.id;
                pendingTimelineTaskScrollAttemptsRef.current = 0;
                pendingTimelineCurrentScrollRef.current = false;
                isProgrammaticTimelineNavigationRef.current = true;
                suppressNextTimelineCurrentScrollRef.current = true;
                setActiveTimelineDateKey(getLocalDateKey(scheduled));
                setActiveTimelineLabel('');
                setSelectedDate(new Date(scheduled));
                setTimelineWindow({ start: -1, end: 1 });
            }
        } else {
            const collection = folders[0]?.collections?.[0];
            if (!collection) {
                alert(t('dailyPlanner.createFolderAndCollectionFirst'));
                return;
            }
            const task: Task = {
                id: generateId(),
                title: newTaskTitle.trim(),
                completed: false,
                type: 'task',
                ...updates,
            };
            updateCollection(collection.id, (current) => ({
                ...current, elements: [...(current.elements || []), task],
            }));
            if (!showSchedule && scheduled && updates.schedule?.precision === 'hour') {
                pendingTimelineTaskScrollRef.current = scheduled;
                pendingTimelineTaskIdScrollRef.current = task.id;
                pendingTimelineTaskScrollAttemptsRef.current = 0;
                pendingTimelineCurrentScrollRef.current = false;
                isProgrammaticTimelineNavigationRef.current = true;
                suppressNextTimelineCurrentScrollRef.current = true;
                setSelectedDate(new Date(scheduled));
                setTimelineScale('day');
                setTimelineWindow({ start: -1, end: 1 });
            }
        }
        setEditingTask(null);
        setEditingCollectionId(null);
        reset();
        setShowAddTask(false);
    };
    const setEditorFromTask = useCallback((task: Task, collectionId: string, date = new Date(task.dueDate!)) => {
        setEditingTask(task);
        setEditingCollectionId(collectionId);
        setNewTaskTitle(task.title || task.task || '');
        setAddTaskDate(date);
        setAddTaskTime(`${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`);
        setAddTaskDuration(Math.max(15, Math.round((getTaskEnd(task).getTime() - date.getTime()) / 60_000)));
        setIsFlexibleTask(Boolean(getFlexiblePrecision(task)));
        setFlexiblePrecision(getFlexiblePrecision(task) || 'day');
        setFlexibleInterval(getFlexibleIntervalForTask(task));
        setFlexibleEndDate(task.schedule?.endDate ? new Date(task.schedule.endDate) : date);
    }, [setAddTaskDate, setAddTaskDuration, setAddTaskTime, setEditingCollectionId, setEditingTask,
        setFlexibleEndDate, setFlexibleInterval, setFlexiblePrecision, setIsFlexibleTask, setNewTaskTitle]);
    const handleEditTask = (task: Task, collectionId: string) => {
        setEditorFromTask(task, collectionId);
        setShowAddTask(true);
    };
    const handleAddTaskForScheduleDate = useCallback((date?: Date, precision: 'hour' | 'day' | 'month' | 'year' = 'day') => {
        reset();
        const target = date ? new Date(date) : new Date();
        setEditingTask(null);
        setEditingCollectionId(null);
        if (precision === 'hour') {
            if (target.getHours() === 0 && target.getMinutes() === 0) target.setHours(9, 0, 0, 0);
            setAddTaskDate(target);
            setAddTaskTime(`${target.getHours().toString().padStart(2, '0')}:${target.getMinutes().toString().padStart(2, '0')}`);
            setShowAddTask(true);
            return;
        }
        setIsFlexibleTask(true);
        setFlexiblePrecision(precision);
        setFlexibleEndDate(normalizeFlexibleDate(target, precision));
        setFixedScheduleDate(date ? target : null);
        setAddTaskDate(target);
        setAddTaskTime('00:00');
        setShowAddTask(true);
    }, [reset, setAddTaskDate, setAddTaskTime, setEditingCollectionId, setEditingTask, setFixedScheduleDate,
        setFlexibleEndDate, setFlexiblePrecision, setIsFlexibleTask, setShowAddTask]);
    const handleCreateTimelineTask = useCallback((date: Date) => {
        const target = getTimelineTaskStartDate(date);
        reset();
        setEditingTask(null);
        setEditingCollectionId(null);
        setAddTaskDate(target);
        setAddTaskTime(`${target.getHours().toString().padStart(2, '0')}:${target.getMinutes().toString().padStart(2, '0')}`);
        setShowAddTask(true);
    }, [reset, setAddTaskDate, setAddTaskTime, setEditingCollectionId, setEditingTask, setShowAddTask]);
    const handleSelectExistingTaskForSchedule = useCallback((taskInfo: PlannerTaskInfo) => {
        if (!existingTaskPickerDate) return;
        const global = existingTaskPickerDate === 'global';
        const date = global ? new Date() : new Date(existingTaskPickerDate);
        if (global || (date.getHours() === 0 && date.getMinutes() === 0)) date.setHours(9, 0, 0, 0);
        setEditorFromTask(taskInfo.task, taskInfo.collectionId, date);
        setFixedScheduleDate(global ? null : date);
        setExistingTaskPickerDate(null);
        setShowAddTask(true);
    }, [existingTaskPickerDate, setEditorFromTask, setExistingTaskPickerDate, setFixedScheduleDate, setShowAddTask]);
    const handleCreateFlexibleTask = useCallback((date: Date, precision: 'day' | 'month' | 'year') => {
        reset();
        const target = normalizeFlexibleDate(date, precision);
        setEditingTask(null);
        setEditingCollectionId(null);
        setAddTaskDate(target);
        setAddTaskTime('00:00');
        setIsFlexibleTask(true);
        setFlexiblePrecision(precision);
        setFlexibleEndDate(target);
        setShowAddTask(true);
    }, [reset, setAddTaskDate, setAddTaskTime, setEditingCollectionId, setEditingTask,
        setFlexibleEndDate, setFlexiblePrecision, setIsFlexibleTask, setShowAddTask]);
    const handleCancelAddTask = () => {
        reset();
        setEditingTask(null);
        setEditingCollectionId(null);
        setShowAddTask(false);
    };
    const handlePickExistingTaskForScheduleDate = useCallback((date?: Date) => {
        setExistingTaskPickerDate(date ? new Date(date) : 'global');
    }, [setExistingTaskPickerDate]);
    return {
        handleAddTask, handleAddTaskForScheduleDate, handleCancelAddTask, handleCreateFlexibleTask,
        handleCreateTimelineTask, handleEditTask, handlePickExistingTaskForScheduleDate,
        handleSelectExistingTaskForSchedule, openCreateTaskModal,
    };
};

export type TaskModalController = ReturnType<typeof useTaskModal>;
